import { useRef, useState } from "react";
import { toast } from "sonner";
import { fetchAuthSession } from "aws-amplify/auth";
import { useLoading } from "../context/LoadingContext";
import { buildWsUrl } from "../utils/wsUrl";

const WS_PING_INTERVAL_MS = 20000;

const WS_ERROR_MESSAGE =
  "There was a problem processing the device comparison. Please try again later.";

const getAuthToken = async () => {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString();
};

/**
 * Custom hook that manages the WebSocket lifecycle for the
 * compare_fda_devices endpoint.
 *
 * @returns {{ compareDevices: Function, isComparing: boolean, abort: Function }}
 */
export function useDeviceComparison() {
  const [isComparing, setIsComparing] = useState(false);
  const { setLoading } = useLoading();

  // Stable refs — never trigger re-renders, safe to read/write inside callbacks
  const wsRef        = useRef(null);
  const keepAliveRef = useRef(null);

  const cleanup = () => {
    if (keepAliveRef.current) {
      clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.onopen    = null;
      wsRef.current.onmessage = null;
      wsRef.current.onerror   = null;
      wsRef.current.onclose   = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsComparing(false);
    setLoading(false);
  };

  /**
   * Imperatively cancel an in-flight comparison.
   */
  const abort = () => {
    cleanup();
  };

  /**
   * Open a WebSocket to compare_fda_devices, send the two device IDs, and
   * call `onSuccess(content)` when the server returns the result.
   *
   * @param {string}   device1Id  510(k) number for Device 1 (subject)
   * @param {string}   device2Id  510(k) number for Device 2 (predicate)
   * @param {Function} onSuccess  Receives the parsed `content` object
   */
  const compareDevices = async (device1Id, device2Id, onSuccess) => {
    if (!device1Id || !device2Id) {
      toast.error("Please select exactly 2 devices to compare.");
      return;
    }

    // Prevent duplicate calls
    if (isComparing) return;

    setIsComparing(true);
    setLoading(true, "Running device comparison...");

    try {
      const token = await getAuthToken();
      const ws = new WebSocket(buildWsUrl("compare_fda_devices", token));
      wsRef.current = ws;

      ws.onopen = () => {
        // Keep-alive ping to survive API Gateway / load-balancer idle timeouts
        keepAliveRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, WS_PING_INTERVAL_MS);

        ws.send(
          JSON.stringify({
            type: "compare_devices",
            payload: {
              device1: device1Id,
              device2: device2Id,
            },
          })
        );

        toast.info("Comparison started. This may take a moment...");
      };

      ws.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);

          // Ignore keep-alive frames
          if (parsed.type === "pong" || parsed.type === "ping") return;

          // Surface backend errors
          if (parsed.error) {
            toast.error(parsed.error || WS_ERROR_MESSAGE);
            cleanup();
            return;
          }

          // Main payload
          if (parsed.content) {
            onSuccess(parsed.content);
            toast.success("Comparison complete — downloading CSV...");
            cleanup();
          }
        } catch (parseErr) {
          // Non-JSON frame (e.g. plain "pong") — safe to ignore
          console.debug("Non-JSON WebSocket frame:", event.data, parseErr);
        }
      };

      ws.onerror = (err) => {
        console.error("WebSocket error:", err);
        toast.error(WS_ERROR_MESSAGE);
        cleanup();
      };

      ws.onclose = () => {
        cleanup();
      };
    } catch (err) {
      console.error("useDeviceComparison error:", err);
      toast.error(WS_ERROR_MESSAGE);
      cleanup();
    }
  };

  return { compareDevices, isComparing, abort };
}
